<script setup>
/**
 * The search box is the filter bar. Whatever is narrowing the ledger sits
 * inside it as a pill you can take off, and the list of everything you *could*
 * filter by opens the moment the box is focused — so nobody has to already know
 * that `tag:` is a thing. Typing narrows that list, and enter takes the
 * highlighted row, which makes `tag:foo`↵ and picking from the list the same
 * gesture.
 *
 * Tags stack ("any of these"); a plan, a goal or unplanned-only is one at a
 * time, because that is what the ledger's focus is.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import Icon from './ui/Icon.vue'
import Kbd from './ui/Kbd.vue'
import { ariaCombo, keysOf } from '../lib/shortcuts'
import {
  applyFilterPatch,
  clearFilter,
  clearFilters,
  filterPills,
  readFilterToken,
  setAmountFilter,
  setDateFilter,
  suggestFilters,
} from '../stores'
import { state } from '../stores/state'
import { t } from '../stores/i18n'
import { currencySymbol } from '../lib/money'

const field = ref(null)
const panel = ref(null)
const open = ref(false)
const cursor = ref(0)

const groups = computed(() => (open.value ? suggestFilters(state.ui.search) : []))
/** The rows as one list, which is what the arrow keys walk. */
const rows = computed(() => groups.value.flatMap((group) => group.rows))
const token = computed(() => readFilterToken(state.ui.search))
const symbol = computed(() => currencySymbol(state.settings.currency))

/**
 * What is being typed into an amount pill, which is not the same thing as the
 * bound it has set so far: binding the field straight to the stored number
 * would rewrite "1" into the box the moment the "1" of "1000" was typed.
 */
const drafts = reactive({})
const amountText = (pill) => drafts[pill.key] ?? pill.value

/** It filters as you go; an empty box is a moment mid-edit, not an instruction. */
const typeAmount = (key, value) => {
  drafts[key] = value
  if (String(value).trim()) setAmountFilter(key, value)
}
const finishAmount = (key, value) => {
  if (String(value).trim()) drafts[key] = String(state.ui[key] ?? '')
  else {
    clearFilter(key)
    delete drafts[key]
  }
}
const dropDraft = (key) => {
  clearFilter(key)
  delete drafts[key]
}

watch(
  () => state.ui.search,
  () => (cursor.value = 0),
)

const focusInput = () => field.value?.focus()
const show = () => {
  open.value = true
  cursor.value = 0
}

function choose(row) {
  if (!row) return
  applyFilterPatch(row.patch)
  state.ui.search = ''
  cursor.value = 0
  nextTick(focusInput)
}

/** Enter takes the highlighted row, or the typed token when the list is shut. */
function commit() {
  if (open.value && rows.value.length) return choose(rows.value[cursor.value])
  if (readFilterToken(state.ui.search)) {
    applyFilterPatch(token.value.patch)
    state.ui.search = ''
  }
}

function move(delta) {
  if (!open.value) return show()
  const count = rows.value.length
  if (!count) return
  cursor.value = (cursor.value + delta + count) % count
}

/** Backspace in an empty box takes the last pill off, as a chip field does. */
function backspace(event) {
  if (state.ui.search || !filterPills.value.length) return
  event.preventDefault()
  clearFilter(filterPills.value[filterPills.value.length - 1].key)
}

const indexOf = (group, row) => rows.value.indexOf(row)

const onOutside = (event) => {
  if (!open.value) return
  if (panel.value?.contains(event.target) || field.value?.closest('div')?.contains(event.target)) return
  open.value = false
}
onMounted(() => document.addEventListener('pointerdown', onOutside))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutside))

/**
 * Esc closes the suggestions; pressed again, it lets go of the field, so the
 * page's single-key shortcuts work again without reaching for the mouse.
 */
function escape(event) {
  if (open.value) open.value = false
  else event.target.blur()
}
</script>

<template>
  <div class="relative">
    <div
      class="input flex cursor-text flex-wrap items-center gap-1.5 !py-1.5 !pr-1.5"
      @click="focusInput"
    >
      <Icon name="search" :size="17" class="shrink-0 text-faint" />

      <!-- Keyed by the filter, not its value: keying by the value would rebuild
           the pill on every keystroke and take the focus with it. -->
      <span
        v-for="pill in filterPills"
        :key="pill.key"
        class="chip chip-on !gap-1 !py-0.5 !pr-1 !pl-2 !text-[12.5px]"
      >
        <span class="text-muted">{{ pill.label }}</span>
        <!-- A bound is a date, so the pill is the picker: tapping it opens the
             one the device already knows how to use. -->
        <input
          v-if="pill.date"
          type="date"
          class="tnum w-[7.5rem] bg-transparent font-semibold outline-none"
          :value="pill.date"
          :aria-label="t('filter.currently', { label: pill.label, value: pill.value })"
          @click.stop
          @change="setDateFilter(pill.key, $event.target.value)"
        />
        <template v-else-if="pill.amount !== undefined">
          <span class="text-faint">{{ symbol }}</span>
          <input
            type="text"
            inputmode="decimal"
            class="tnum w-[5rem] bg-transparent font-semibold outline-none"
            :value="amountText(pill)"
            :aria-label="t('filter.currently', { label: pill.label, value: pill.value })"
            @click.stop
            @input="typeAmount(pill.key, $event.target.value)"
            @change="finishAmount(pill.key, $event.target.value)"
            @keydown.enter.stop="$event.target.blur()"
          />
        </template>
        <span v-else class="max-w-[12rem] truncate font-semibold">{{ pill.value }}</span>
        <button
          class="rounded p-0.5 hover:bg-accent/20"
          :aria-label="t('filter.remove', { label: pill.label.toLowerCase() })"
          @click.stop="dropDraft(pill.key)"
        >
          <Icon name="x" :size="12" />
        </button>
      </span>

      <input
        ref="field"
        v-model="state.ui.search"
        class="min-w-[7rem] flex-1 bg-transparent py-1 text-[15px] outline-none placeholder:text-faint"
        type="text"
        inputmode="search"
        role="combobox"
        aria-controls="filter-suggestions"
        :aria-expanded="open"
        :placeholder="filterPills.length ? t('filter.searchShort') : t('filter.search')"
        :aria-label="t('filter.searchLabel')"
        :aria-keyshortcuts="ariaCombo(keysOf('search'))"
        data-search
        @focus="show"
        @keydown.enter.prevent="commit"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.esc="escape"
        @keydown.delete="backspace"
      />
      <!-- The key that brings you here, while there is nothing typed to hide it behind. -->
      <Kbd v-if="!state.ui.search && !open" :combo="keysOf('search')" />

      <button
        v-if="state.ui.search"
        class="btn-ghost !px-1.5 !py-1"
        :aria-label="t('filter.clearSearch')"
        @click.stop="state.ui.search = ''"
      >
        <Icon name="x" :size="16" />
      </button>
      <button
        class="btn-ghost !px-1.5 !py-1"
        :class="open && 'text-accent'"
        :aria-label="t('filter.add')"
        :aria-expanded="open"
        @click.stop="open ? (open = false) : (show(), focusInput())"
      >
        <Icon name="plus" :size="16" />
      </button>
    </div>

    <div
      v-if="open"
      id="filter-suggestions"
      ref="panel"
      class="scroll-thin absolute top-full right-0 left-0 z-40 mt-1.5 max-h-[20rem] overflow-y-auto
             rounded-xl border border-line bg-surface p-1.5 shadow-[var(--c-shadow)] sm:left-auto sm:w-80"
      role="listbox"
    >
      <template v-for="group in groups" :key="group.key">
        <p class="px-2 pt-1 pb-1 text-[11px] font-semibold tracking-wide text-faint uppercase">
          {{ group.title }}
        </p>
        <button
          v-for="row in group.rows"
          :key="group.key + row.id"
          class="menu-row"
          :class="[
            indexOf(group, row) === cursor && 'bg-raised',
            row.on && 'text-accent',
          ]"
          role="option"
          :aria-selected="row.on"
          @mouseenter="cursor = indexOf(group, row)"
          @click="choose(row)"
        >
          <span class="truncate">{{ row.name }}</span>
          <span class="flex shrink-0 items-center gap-2">
            <span class="text-[11px] text-faint">{{ group.key }}:</span>
            <Icon v-if="row.on" name="check" :size="14" />
          </span>
        </button>
      </template>

      <p v-if="!groups.length" class="px-2 py-2 text-[12.5px] text-muted">
        {{ t('filter.noMatch') }}
      </p>

      <button
        v-if="filterPills.length || state.ui.search"
        class="menu-row mt-1 border-t border-line-soft pt-2 text-muted"
        @click="clearFilters(); open = false"
      >
        {{ t('filter.clearAll') }}
      </button>
    </div>
  </div>
</template>
